import React, {FC, memo, useMemo, useState} from "react";
import {
    ArrayDataOptions,
    Maybe,
    SortOrder,
    TablesEnum,
    useGetEmailNotificationsByBookingIdQuery,
} from "../../../generated/graphql";
import SentMailsList from "../../notifications/SentMailsList";
import {TableType} from "../../notifications/SentMailsListColumns";
import PanelCard from "../../../views/PanelCard/PanelCard";
import {groupNotificationsByMessageId} from "./groupNotificationsByMessageId";

type CommunicationHistoryBookingProps = {
    bookingId: string;
};

const standardColumns: Array<keyof TableType> = [
    "actions",
    "createDate",
    "status",
    "receiverName",
    "email",
    "subject",
    "text",
    "templateName",
    "attachments",
];

const CommunicationHistoryBooking: FC<CommunicationHistoryBookingProps> = ({
    bookingId,
}) => {
    const [totalResults, setTotalResults] = useState<number>(0);
    const [options, setOptions] = useState<Maybe<ArrayDataOptions>>({
        filter: [],
        limit: 50,
        offset: 0,
        sortOrder: SortOrder.Descending,
    });

    const {
        data,
        loading,
        refetch,
    } = useGetEmailNotificationsByBookingIdQuery({
        variables: {bookingId},
        fetchPolicy: "cache-and-network",
    });

    const notifications = data?.notificationsByBooking ?? [];

    // G5: rein lokal + IMMUTABLE — data.notificationsByBooking wird NIE mutiert, damit der
    // von Apollo deduplizierte Cache des InvoiceSentStatusTagContainer unberührt bleibt.
    // groupNotificationsByMessageId baut neue Arrays/Objekte und fasst pro physischer Mail
    // (gemeinsame messageID) genau eine Zeile zusammen.
    const groupedRows = useMemo(
        () =>
            groupNotificationsByMessageId(
                notifications as unknown as Array<TableType>,
            ),
        // Nur bei neuen Daten neu rechnen (stabile Referenz aus Apollo).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data?.notificationsByBooking],
    );

    const tableData = {
        dataSource: groupedRows,
        // G7: Footer zeigt die Zeilenzahl, nicht die Roh-Notification-Zahl.
        total: groupedRows.length,
        loading,
    };

    return (
        <PanelCard title={"Kommunikationshistorie"} columns={1} gapAfter>
            <SentMailsList
                tableData={tableData}
                tablesEnum={TablesEnum.NotificationsByBookingId}
                options={options}
                setOptions={setOptions}
                hidePagination
                hideSortFilter
                standardColumns={standardColumns}
                totalResults={totalResults}
                setTotalResults={setTotalResults}
                refetch={refetch}
            />
        </PanelCard>
    );
};

export default memo(CommunicationHistoryBooking);
